import { forwardRef, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { AlertCircle, CheckCircle2, Play } from "lucide-react";
import { Banner, Button, SidePanel } from "@/components/ds";
import { Switch } from "@/components/ui/switch";
import { splashFileName, type SplashPreviewSource } from "@/components/app-splashes/SplashPreview";
import { useCreateAppSplash, useUpdateAppSplash } from "@/hooks/useAppSplashes";
import { isNetworkError, uploadErrorMessage, useFeedMediaUpload } from "@/hooks/useFeedMediaUpload";
import {
  DEFAULT_SPLASH_END_TIME,
  DEFAULT_SPLASH_START_TIME,
  dubaiToUtcIso,
  utcToDubaiParts,
} from "@/lib/dubaiDate";
import {
  checkSplashFile,
  formatSplashSize,
  SPLASH_SAMPLE_FILES,
  type SplashFileCheck,
  type VideoMetadataLoader,
} from "@/lib/splash/checkSplashFile";
import { isSplashFileSentence, splashServerMessage } from "@/lib/splash/serverMessage";
import type { AppSplashAppResponse, AppSplashRequest, AppSplashResponse } from "@/types/generated/cms.generated";

export const AMPLIFY_NOTE = "Amplify+ phones use this from their next app update";
export const SAMPLE_LABEL = "Example only — nothing is uploaded";
const END_BEFORE_START = "The end must be after the start.";
const FILE_HELP = "MP4 up to 4 s and 5 MB, upright 9:16 · or a Lottie .json up to 1 MB.";

/** "<name> (<package>)" — how the page names an app (SP-D21). */
export const appOptionLabel = (app: AppSplashAppResponse) => `${app.name ?? ""} (${app.appScheme ?? ""})`;

/** Any Amplify+ package, Dev too (`dev.athlyn.amplifyplus`). */
export const isAmplifyApp = (appScheme: string) => appScheme.endsWith("amplifyplus");

/**
 * The app a new splash (and the explainer) starts on: the filter's app when one is chosen, else
 * the first Athlyn package (not Amplify+), else the first app.
 */
export function defaultSplashApp(apps: AppSplashAppResponse[], filter: string): AppSplashAppResponse | undefined {
  return (
    apps.find((a) => a.appScheme === filter) ??
    apps.find((a) => a.appScheme && !isAmplifyApp(a.appScheme)) ??
    apps[0]
  );
}

const INPUT =
  "h-[34px] w-full rounded-lg border border-ds-line2 bg-ds-panel px-2.5 font-ds text-[13.5px] text-ds-ink outline-none focus-visible:ring-2 focus-visible:ring-ds-accent";

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-ds-ink">
        {label}
      </label>
      {children}
    </div>
  );
}

const Message = forwardRef<HTMLParagraphElement, { children: ReactNode; testId: string }>(function Message(
  { children, testId },
  ref
) {
  return (
    <p ref={ref} role="alert" className="text-[12.5px] text-ds-err" data-testid={testId}>
      {children}
    </p>
  );
});

interface SplashFormPanelProps {
  open: boolean;
  /** The splash being edited, or `null` to add one. */
  splash: AppSplashResponse | null;
  apps: AppSplashAppResponse[];
  onClose: () => void;
  onPreview: (source: SplashPreviewSource) => void;
  /** Injectable for tests; the browser's `<video>` metadata otherwise. */
  loadVideoMetadata?: VideoMetadataLoader;
  /** Returns focus to the opener on close. */
  onCloseAutoFocus?: (event: Event) => void;
  /** The App a new splash starts on (see `defaultSplashApp`). */
  defaultAppScheme?: string;
}

/**
 * Add / Edit side panel (splash-cms §1 part 4). Live file checks before upload (Save stays off
 * until the picked file passes), "Try a file" samples that only simulate, dates in Dubai time sent
 * as UTC ISO, and every server refusal shown in the server's own words.
 */
export function SplashFormPanel({
  open,
  splash,
  apps,
  onClose,
  onPreview,
  loadVideoMetadata,
  onCloseAutoFocus,
  defaultAppScheme,
}: SplashFormPanelProps) {
  const ids = { name: useId(), app: useId(), file: useId(), sd: useId(), st: useId(), ed: useId(), et: useId(), on: useId() };
  const editing = splash !== null;
  const start = utcToDubaiParts(splash?.startsAt);
  const end = utcToDubaiParts(splash?.endsAt);

  const [name, setName] = useState(splash?.name ?? "");
  const [appScheme, setAppScheme] = useState(splash?.appScheme ?? defaultAppScheme ?? apps[0]?.appScheme ?? "");
  const [startDate, setStartDate] = useState(start.date);
  const [startTime, setStartTime] = useState(start.time || DEFAULT_SPLASH_START_TIME);
  const [endDate, setEndDate] = useState(end.date);
  const [endTime, setEndTime] = useState(end.time || DEFAULT_SPLASH_END_TIME);
  const [isActive, setIsActive] = useState(splash?.isActive ?? true);
  const [file, setFile] = useState<File | null>(null);
  const [check, setCheck] = useState<SplashFileCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [sampleMessage, setSampleMessage] = useState<string | null>(null);
  const [fileServerError, setFileServerError] = useState<string | null>(null);
  const [formServerError, setFormServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // The blob a picked file was already uploaded to: a refused save (overlap, dates, name) retries
  // with it instead of uploading the same file again (which would orphan a public blob).
  const [uploaded, setUploaded] = useState<{ file: File; blobUrl: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const fileMessageRef = useRef<HTMLParagraphElement>(null);
  const formErrorRef = useRef<HTMLDivElement>(null);
  const checkRun = useRef(0);

  const { uploadImage } = useFeedMediaUpload("/app-splashes/upload-url", { errorLabel: "file" });
  const create = useCreateAppSplash();
  const update = useUpdateAppSplash();

  const startsAt = dubaiToUtcIso(startDate, startTime);
  const endsAt = dubaiToUtcIso(endDate, endTime);
  const datesOrdered = startsAt !== null && endsAt !== null && Date.parse(endsAt) > Date.parse(startsAt);
  const datesError = startsAt !== null && endsAt !== null && !datesOrdered ? END_BEFORE_START : null;

  // Editing keeps the current file unless a new one is picked; a new file must pass its check.
  const fileOk = file ? check?.ok === true : editing;
  const canSave =
    name.trim().length > 0 && appScheme !== "" && datesOrdered && fileOk && !checking && sampleMessage === null && !saving;

  const onPickFile = async (picked: File | null) => {
    setSampleMessage(null);
    setFileServerError(null);
    setUploaded(null);
    setFile(picked);
    setCheck(null);
    if (!picked) return;
    const run = ++checkRun.current;
    setChecking(true);
    const result = await checkSplashFile(picked, loadVideoMetadata);
    if (run !== checkRun.current) return; // a later pick replaced this one
    setCheck(result);
    setChecking(false);
  };

  const keepCurrentFile = () => {
    checkRun.current++;
    setFile(null);
    setCheck(null);
    setChecking(false);
    setFileServerError(null);
    setUploaded(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const trySample = async (sampleId: string) => {
    const sample = SPLASH_SAMPLE_FILES.find((s) => s.id === sampleId);
    if (!sample) return;
    const result = await checkSplashFile(sample.file, sample.loadVideoMetadata);
    setSampleMessage(result.ok ? null : result.error);
  };

  const onSave = async () => {
    if (!canSave || startsAt === null || endsAt === null) return;
    setSaving(true);
    setFileServerError(null);
    setFormServerError(null);
    try {
      let blobUrl = splash?.blobUrl ?? "";
      let mediaType = splash?.mediaType ?? "Video";
      if (file && check?.ok) {
        try {
          if (uploaded?.file === file) {
            blobUrl = uploaded.blobUrl;
          } else {
            blobUrl = await uploadImage(file, check.contentType);
            setUploaded({ file, blobUrl });
          }
        } catch (error) {
          // The server's own sentence (errorDetails) when it answered; the friendly one when it never did.
          setFileServerError(
            isNetworkError(error) ? uploadErrorMessage(error, "file") : splashServerMessage(error, uploadErrorMessage(null, "file"))
          );
          return;
        }
        mediaType = check.mediaType;
      }

      const body: AppSplashRequest = { appScheme, name: name.trim(), mediaType, blobUrl, startsAt, endsAt, isActive };
      try {
        if (splash?.id) await update.mutateAsync({ id: splash.id, data: body });
        else await create.mutateAsync(body);
      } catch (error) {
        const sentence = splashServerMessage(error, "Failed to save the splash. Please try again.");
        if (isSplashFileSentence(sentence)) setFileServerError(sentence);
        else setFormServerError(sentence);
        return;
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const fileMessage = sampleMessage ?? fileServerError ?? (check && !check.ok ? check.error : null);

  // A server refusal can land below the fold (the banner sits just above the sticky footer on a
  // phone): bring it into view when it appears.
  useEffect(() => {
    if (formServerError) formErrorRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [formServerError]);
  useEffect(() => {
    if (fileServerError) fileMessageRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [fileServerError]);
  const currentFileName = splashFileName(splash?.blobUrl);

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => !next && !saving && onClose()}
      title={editing ? "Edit splash" : "Add splash"}
      onCloseAutoFocus={onCloseAutoFocus}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void onSave()} disabled={!canSave} loading={saving} data-testid="splash-save">
            {editing ? "Save changes" : "Add splash"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5" data-testid="splash-form">
        <p className="text-[13px] text-ds-ink2">
          {editing ? "Changes reach phones from their next-but-one open." : "Schedule a splash for an occasion."}
        </p>

        <Field label="Name" htmlFor={ids.name}>
          <input
            id={ids.name}
            className={INPUT}
            value={name}
            maxLength={120}
            placeholder="e.g. UAE National Day"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field label="App" htmlFor={ids.app}>
          <select id={ids.app} className={INPUT} value={appScheme} onChange={(e) => setAppScheme(e.target.value)}>
            {apps.map((app) => (
              <option key={app.appScheme ?? ""} value={app.appScheme ?? ""}>
                {appOptionLabel(app)}
              </option>
            ))}
          </select>
          {isAmplifyApp(appScheme) && (
            <p className="text-[12.5px] text-ds-warn" data-testid="splash-amplify-note">
              {AMPLIFY_NOTE}
            </p>
          )}
        </Field>

        <Field label="File" htmlFor={ids.file}>
          <div className="flex flex-col gap-2 rounded-ds border border-dashed border-ds-line2 p-3">
            {editing && !file && (
              <p className="text-[13px] text-ds-ink2" data-testid="splash-current-file">
                Current file: {currentFileName} · {splash?.mediaType === "Lottie" ? "Lottie" : "Video"}
                {splash?.sizeBytes ? ` · ${formatSplashSize(splash.sizeBytes)}` : ""}
              </p>
            )}
            {check?.ok && (
              <p className="flex items-center gap-1.5 text-[13px] text-ds-ok" data-testid="splash-file-ok">
                <CheckCircle2 className="h-4 w-4 flex-none" aria-hidden="true" />
                {check.summary}
              </p>
            )}
            {checking && <p className="text-[13px] text-ds-ink3">Checking the file…</p>}
            <input
              ref={fileInput}
              id={ids.file}
              type="file"
              accept="video/mp4,.mp4,application/json,.json"
              className="text-[13px] text-ds-ink2 file:mr-2 file:rounded-lg file:border file:border-ds-line2 file:bg-ds-panel file:px-2.5 file:py-1 file:font-ds file:text-ds-ink"
              onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
              data-testid="splash-file-input"
            />
            <p className="text-[12px] text-ds-ink3">{FILE_HELP}</p>
            <div className="flex flex-wrap gap-2">
              {check?.ok && file && (
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Play className="h-3.5 w-3.5" aria-hidden="true" />}
                  onClick={() => onPreview({ kind: "file", file, mediaType: check.mediaType, title: name.trim() || file.name })}
                >
                  Preview file
                </Button>
              )}
              {editing && file && (
                <Button size="sm" variant="ghost" onClick={keepCurrentFile}>
                  Keep the current file
                </Button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ds-line pt-2" data-testid="splash-samples">
              <span className="text-[12px] text-ds-ink3">Try a file:</span>
              {SPLASH_SAMPLE_FILES.map((sample) => (
                <Button key={sample.id} size="sm" variant="secondary" onClick={() => void trySample(sample.id)}>
                  {sample.label}
                </Button>
              ))}
              <span className="text-[12px] italic text-ds-ink3">{SAMPLE_LABEL}</span>
              {sampleMessage && (
                <Button size="sm" variant="ghost" onClick={() => setSampleMessage(null)}>
                  Clear example
                </Button>
              )}
            </div>
          </div>
          {fileMessage && (
            <Message ref={fileMessageRef} testId="splash-file-message">
              {fileMessage}
            </Message>
          )}
        </Field>

        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-[13px] font-semibold text-ds-ink">Starts (Dubai time)</legend>
          <div className="grid grid-cols-[1fr_110px] gap-2">
            <input id={ids.sd} aria-label="Start date" type="date" className={INPUT} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <input id={ids.st} aria-label="Start time" type="time" className={INPUT} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
        </fieldset>
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-[13px] font-semibold text-ds-ink">Ends (Dubai time)</legend>
          <div className="grid grid-cols-[1fr_110px] gap-2">
            <input id={ids.ed} aria-label="End date" type="date" className={INPUT} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            <input id={ids.et} aria-label="End time" type="time" className={INPUT} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </div>
          {datesError && <Message testId="splash-dates-message">{datesError}</Message>}
        </fieldset>

        <div className="flex items-center justify-between gap-3 rounded-ds border border-ds-line p-3">
          <div>
            <label htmlFor={ids.on} className="text-[13px] font-semibold text-ds-ink">
              On
            </label>
            <p className="text-[12.5px] text-ds-ink3">When off, phones ignore it.</p>
          </div>
          <Switch
            id={ids.on}
            checked={isActive}
            onCheckedChange={setIsActive}
            className="data-[state=checked]:bg-ds-accent data-[state=unchecked]:bg-ds-line2 focus-visible:ring-ds-accent"
          />
        </div>

        {formServerError && (
          <div ref={formErrorRef} className="scroll-mb-4" data-testid="splash-form-error-region">
            <Banner tone="err" icon={AlertCircle}>
              <span data-testid="splash-form-error">{formServerError}</span>
            </Banner>
          </div>
        )}
      </div>
    </SidePanel>
  );
}
